import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api, type ExecutiveMemoListItem, type ExecutiveMemoPriority } from "../lib/api";
import { logError, toUserMessage } from "../lib/errors";
import {
  dateTimeLocalToIso,
  formatExecutiveMemoDateTime,
  formatExecutiveMemoKindLabel,
  formatExecutiveMemoPriorityLabel,
  parseCommaSeparatedValues,
} from "../lib/executiveMemos";

type SortValue = "captured_at_desc" | "captured_at_asc" | "updated_at_desc";

type Filters = {
  q: string;
  kind: string;
  theme: string;
  tag: string;
  priority: ExecutiveMemoPriority[];
  from: string;
  to: string;
  sort: SortValue;
};

const INITIAL_FILTERS: Filters = {
  q: "",
  kind: "",
  theme: "",
  tag: "",
  priority: [],
  from: "",
  to: "",
  sort: "captured_at_desc",
};

function togglePriority(values: ExecutiveMemoPriority[], priority: ExecutiveMemoPriority) {
  return values.includes(priority)
    ? values.filter((value) => value !== priority)
    : [...values, priority];
}

function getMemoTitle(memo: ExecutiveMemoListItem) {
  return memo.title || memo.summary || memo.bodyPreview || "無題のメモ";
}

function isFiltered(f: Filters) {
  return (
    f.q !== "" ||
    f.kind !== "" ||
    f.theme !== "" ||
    f.tag !== "" ||
    f.priority.length > 0 ||
    f.from !== "" ||
    f.to !== ""
  );
}

export default function ExecutiveMemosPage() {
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(INITIAL_FILTERS);
  const [items, setItems] = useState<ExecutiveMemoListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadMemos = useCallback(
    async (targetFilters: Filters, cursor?: string) => {
      if (cursor) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setErrorMessage(null);

      try {
        const response = await api.listExecutiveMemos({
          q: targetFilters.q.trim() || undefined,
          kind: parseCommaSeparatedValues(targetFilters.kind),
          theme: parseCommaSeparatedValues(targetFilters.theme),
          tag: parseCommaSeparatedValues(targetFilters.tag),
          priority: targetFilters.priority,
          from: dateTimeLocalToIso(targetFilters.from ? `${targetFilters.from}T00:00` : ""),
          to: dateTimeLocalToIso(targetFilters.to ? `${targetFilters.to}T23:59` : ""),
          sort: targetFilters.sort,
          limit: 20,
          cursor,
        });

        setItems((prev) => (cursor ? [...prev, ...response.items] : response.items));
        setNextCursor(response.nextCursor ?? null);
      } catch (error) {
        logError(error);
        setErrorMessage(toUserMessage(error));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  useEffect(() => {
    void loadMemos(INITIAL_FILTERS);
  }, [loadMemos]);

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    void loadMemos(filters);
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    void loadMemos(INITIAL_FILTERS);
  };

  const filtered = isFiltered(appliedFilters);

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-2xl">
        <header className="mb-6 flex items-center justify-between">
          <Link to="/request" className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ←戻る
          </Link>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">社長メモ</p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="rounded-[2rem] border border-white/80 bg-white/90 p-5 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-black tracking-[0.12em] text-[#A67C52]">社長メモ管理</p>
              <h1 className="mt-3 text-2xl font-black tracking-tight text-[#7C5A4E]">社長メモ</h1>
            </div>
            <Link
              to="/request/executive-memos"
              className="inline-flex min-h-[40px] items-center justify-center rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#5D4D46]"
            >
              新しいメモを追加
            </Link>
          </div>

          <div className="mt-4 rounded-2xl border border-[#E8D5C8] bg-[#FFFAF7] p-4">
            <p className="mb-3 text-xs font-black tracking-wide text-[#A67C52]">絞り込み</p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">検索</label>
                <input
                  value={filters.q}
                  maxLength={200}
                  onChange={(event) => setFilters((prev) => ({ ...prev, q: event.target.value }))}
                  className="w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">種別</label>
                <input
                  value={filters.kind}
                  onChange={(event) => setFilters((prev) => ({ ...prev, kind: event.target.value }))}
                  placeholder="戦略, 商談"
                  className="w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">テーマ</label>
                <input
                  value={filters.theme}
                  onChange={(event) => setFilters((prev) => ({ ...prev, theme: event.target.value }))}
                  placeholder="相談, 調査"
                  className="w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">タグ</label>
                <input
                  value={filters.tag}
                  onChange={(event) => setFilters((prev) => ({ ...prev, tag: event.target.value }))}
                  placeholder="顧客A, 採用"
                  className="w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">並び順</label>
                <select
                  value={filters.sort}
                  onChange={(event) => setFilters((prev) => ({ ...prev, sort: event.target.value as SortValue }))}
                  className="w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none"
                >
                  <option value="captured_at_desc">撮影日が新しい順</option>
                  <option value="captured_at_asc">撮影日が古い順</option>
                  <option value="updated_at_desc">更新日が新しい順</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">撮影日（開始）</label>
                <input
                  type="date"
                  value={filters.from}
                  onChange={(event) => setFilters((prev) => ({ ...prev, from: event.target.value }))}
                  className="w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">撮影日（終了）</label>
                <input
                  type="date"
                  value={filters.to}
                  onChange={(event) => setFilters((prev) => ({ ...prev, to: event.target.value }))}
                  className="w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none"
                />
              </div>
            </div>
            <div className="mt-3">
              <p className="mb-2 text-xs font-bold text-[#7C5A4E]">優先度</p>
              <div className="flex flex-wrap gap-4">
                {(["high", "mid", "low"] as ExecutiveMemoPriority[]).map((priority) => (
                  <label key={priority} className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-bold text-[#5D4D46]">
                    <input
                      type="checkbox"
                      checked={filters.priority.includes(priority)}
                      onChange={() =>
                        setFilters((prev) => ({
                          ...prev,
                          priority: togglePriority(prev.priority, priority),
                        }))
                      }
                      className="accent-[#A67C52]"
                    />
                    {formatExecutiveMemoPriorityLabel(priority)}
                  </label>
                ))}
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={handleApplyFilters}
                disabled={loading}
                className="flex-1 rounded-full bg-[#A67C52] py-2 text-sm font-black text-white hover:bg-[#7C5A4E] disabled:opacity-50"
              >
                絞り込む
              </button>
              <button
                type="button"
                onClick={handleClearFilters}
                disabled={loading}
                className="rounded-full border border-[#A67C52] px-4 py-2 text-sm font-black text-[#A67C52] hover:bg-[#F5EDE8] disabled:opacity-50"
              >
                リセット
              </button>
            </div>
          </div>

          {!loading && errorMessage && (
            <p className="mt-4 rounded-2xl bg-[#FFF0EB] p-3 text-sm font-bold text-[#B4533A]">{errorMessage}</p>
          )}
        </section>

        <section
          aria-label="社長メモ一覧"
          className="mt-5 rounded-[2rem] border border-[#EADBC8] bg-white/80 p-4 shadow-[0_18px_48px_rgba(124,90,78,0.12)] sm:p-5"
        >
          {!loading && items.length > 0 && (
            <p className="mb-3 text-xs text-[#9C867D]">
              {filtered ? "絞り込み結果：" : "全"}
              {items.length}件{nextCursor ? "以上" : ""}
            </p>
          )}

          {loading ? (
            <p className="py-10 text-center text-sm text-[#9C867D]">読み込み中...</p>
          ) : !errorMessage && items.length === 0 ? (
            <div className="flex min-h-[240px] items-center justify-center rounded-2xl border border-dashed border-[#EADBC8] bg-[#FFFCFA] p-5 text-center text-sm leading-7 text-[#6B5851]">
              {filtered ? "条件に一致するメモがありません。" : "まだ社長メモがありません。最初の1枚を撮ってみましょう。"}
            </div>
          ) : (
            <div className="grid gap-3">
              {items.map((memo) => (
                <article key={memo.id} className="overflow-hidden rounded-2xl border border-[#EADBC8] bg-white p-4 shadow-sm">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0 flex-1">
                      <h2 className="text-base font-black text-[#5D4D46]">{getMemoTitle(memo)}</h2>
                      <p className="mt-2 line-clamp-2 text-sm leading-6 text-[#6B5851]">
                        {memo.bodyPreview || memo.summary}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {memo.kind ? <span className="rounded-full bg-[#F2EDE5] px-2.5 py-1 text-xs font-bold text-[#7C5A4E]">{formatExecutiveMemoKindLabel(memo.kind)}</span> : null}
                        {memo.themes.slice(0, 3).map((theme) => (
                          <span key={theme} className="rounded-full bg-[#F4E8DC] px-2.5 py-1 text-xs font-bold text-[#A67C52]">
                            {theme}
                          </span>
                        ))}
                        {memo.themes.length > 3 ? (
                          <span className="rounded-full bg-[#F4E8DC] px-2.5 py-1 text-xs font-bold text-[#A67C52]">
                            +{memo.themes.length - 3}
                          </span>
                        ) : null}
                        {memo.tags.map((tag) => (
                          <span key={tag} className="rounded-full bg-[#EFF3F8] px-2.5 py-1 text-xs font-bold text-[#5D7080]">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-[#8A746A]">
                        <span className="font-bold text-[#7C5A4E]">優先度 {formatExecutiveMemoPriorityLabel(memo.priority)}</span>
                        <span className="ml-2">{formatExecutiveMemoDateTime(memo.capturedAt)}</span>
                      </div>
                    </div>
                    <Link
                      to={`/executive-memos/${memo.id}`}
                      className="min-h-[42px] w-full shrink-0 rounded-full bg-[#7C5A4E] px-4 py-2 text-center text-sm font-black text-white shadow-sm transition hover:bg-[#5D4D46] sm:w-auto"
                    >
                      詳細を見る
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}

          {nextCursor ? (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => void loadMemos(appliedFilters, nextCursor)}
                disabled={loadingMore}
                className="rounded-full border border-[#D8C0AF] bg-white px-6 py-2.5 text-sm font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2] disabled:opacity-50"
              >
                {loadingMore ? "読み込み中..." : "もっと見る"}
              </button>
            </div>
          ) : null}
        </section>
      </div>
    </main>
  );
}
